import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import { Heading, Text } from '~/components/core';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import ArrayFieldFormik from '~/components/core/Formik/NestedFieldFormik/ArrayFieldFormik';
import AddIcon from '~/components/icons/AddIcon';
import type {
  ExcludedEmail,
  ExcludedWord,
  NotificationSuppressionBuilderProps,
  NotificationSuppressionData,
} from '~/components/SystemConfiguration/EmailConfiguration/emailConfigurationTypes';
import TextFieldFormik from '~/components/TextFieldFormik';
import Divider from '~/components/ui/atoms/Divider';

const EmailNotificationSuppressionBuilderContainer: React.FC<NotificationSuppressionBuilderProps> = ({
  onClose,
  onNotificationSuppressionRuleSubmit,
  emailNotificationSuppressionData,
}) => {
  return (
    <CardDialog isDialog title="Notification Suppression Rules" onClose={onClose} fullWidth maxWidth="md">
      <Heading variant={Heading.TYPES.H4} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        Manage rules to suppress email notifications for incoming communications which do not require adjuster action.
      </Heading>
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        <span className="block">
          All conditions are combined using an <span className="font-bold">AND</span> logic. At least one filter must be
          applied.{' '}
          <span className="block">
            <span className="font-bold">Note:</span> Suppression rules apply <span className="font-bold">only</span> to
            emails that are attached to a claim
          </span>
        </span>
      </Text>
      <Formik
        initialValues={{
          id: emailNotificationSuppressionData?.id || undefined,
          display_name: emailNotificationSuppressionData?.display_name || '',
          check_to_field: emailNotificationSuppressionData?.check_to_field || false,
          check_cc_field: emailNotificationSuppressionData?.check_cc_field || false,
          check_bcc_field: emailNotificationSuppressionData?.check_bcc_field || false,
          excluded_words: emailNotificationSuppressionData?.excluded_words || [],
          excluded_emails: emailNotificationSuppressionData?.excluded_emails || [],
          is_enabled:
            emailNotificationSuppressionData?.is_enabled !== undefined
              ? emailNotificationSuppressionData.is_enabled
              : true,
          at_least_one_word_or_email: false,
        }}
        validationSchema={Yup.object().shape({
          display_name: Yup.string().trim().required('Required'),
          check_to_field: Yup.boolean(),
          check_cc_field: Yup.boolean(),
          check_bcc_field: Yup.boolean().test(
            'at-least-one-check',
            'At least one of "To", "CC", or "BCC" must be selected',
            function (value) {
              const { check_to_field, check_cc_field } = this.parent;
              return check_to_field || check_cc_field || value;
            }
          ),
          excluded_words: Yup.array().of(
            Yup.object().shape({
              excluded_word: Yup.string().required("Keyword can't be empty").nullable(false),
            })
          ),
          excluded_emails: Yup.array().of(
            Yup.object().shape({
              excluded_email: Yup.string()
                .email('Invalid email format')
                .required("Email input can't be empty")
                .nullable(false),
            })
          ),
          at_least_one_word_or_email: Yup.boolean().when(['excluded_words', 'excluded_emails'], {
            is: (excluded_words: ExcludedWord[], excluded_emails: ExcludedEmail[]) => {
              const hasValidWord = Array.isArray(excluded_words)
                ? excluded_words.length > 0 && excluded_words.some((word) => word?.excluded_word?.trim())
                : false;

              const hasValidEmail = Array.isArray(excluded_emails)
                ? excluded_emails.length > 0 && excluded_emails.some((email) => email?.excluded_email?.trim())
                : false;

              return !(hasValidWord || hasValidEmail);
            },
            then: Yup.boolean().oneOf([true], 'At least one keyword or email must be provided'),
            otherwise: Yup.boolean(),
          }),
        })}
        onSubmit={async (values: NotificationSuppressionData, { setSubmitting }) => {
          const submitValues = { ...values };
          delete submitValues.at_least_one_word_or_email;

          try {
            await onNotificationSuppressionRuleSubmit(submitValues);
            onClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => {
          return (
            <div className="mt-24 flex flex-col gap-20">
              <TextFieldFormik id="display_name" label="Rule Name" />
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.REGULAR} className="mt-20 font-bold">
                Suppress notification if recipient is listed in:
              </Text>
              <CheckboxFormik
                id="check_to_field"
                label={
                  <div className="inline-flex items-baseline">
                    <span className="font-bold">TO</span>&nbsp;field
                  </div>
                }
              />
              <CheckboxFormik
                id="check_cc_field"
                label={
                  <div className="inline-flex items-baseline">
                    <span className="font-bold">CC</span>&nbsp;field
                  </div>
                }
              />
              <CheckboxFormik
                id="check_bcc_field"
                label={
                  <div className="inline-flex items-baseline">
                    <span className="font-bold">BCC</span>&nbsp;field
                  </div>
                }
              />
              <Divider className="my-8" />
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.REGULAR}>
                Don&apos;t create notification when sender is
              </Text>
              <Text
                variant={Text.VARIANTS.SM}
                weight={Text.WEIGHTS.REGULAR}
                colorVariant={Text.COLOR_VARIANTS.SECONDARY}
              >
                You can use wildcards to exclude entire domains (e.g., *@company.com) or specific email patterns. Emails
                matching these will not trigger an acknowledgment.
              </Text>
              <ArrayFieldFormik
                fieldId="excluded_emails"
                innerObjectConfig={[
                  {
                    id: 'excluded_email',
                    label: 'Enter email',
                    type: 'text',
                  },
                ]}
                initialNewItemValues={{
                  excluded_email: '',
                }}
                allowDeletingInitialValues
                addNewItemButtonText="add another email"
                addNewItemButtonStartIcon={<AddIcon iconColor="currentColor" />}
              />
              <Divider className="my-8" />
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.REGULAR}>
                Don&apos;t create notification when email subject includes at least one of the following keywords{' '}
              </Text>
              <ArrayFieldFormik
                fieldId="excluded_words"
                innerObjectConfig={[
                  {
                    id: 'excluded_word',
                    label: 'Enter keyword',
                    type: 'text',
                  },
                ]}
                initialNewItemValues={{
                  excluded_word: '',
                }}
                allowDeletingInitialValues
                addNewItemButtonText="add another keyword"
                addNewItemButtonStartIcon={<AddIcon iconColor="currentColor" />}
              />
              <ErrorHelperTextFormik id="at_least_one_word_or_email" />
              <div className="mt-16 flex w-full justify-end">
                <DialogFooterActions
                  primaryLabel="Save"
                  onClickPrimary={handleSubmit}
                  onClickSecondary={onClose}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          );
        }}
      </Formik>
    </CardDialog>
  );
};

export default EmailNotificationSuppressionBuilderContainer;
